import React, { useState } from 'react';
import axios from 'axios';
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';

const LoginPage = ({ onLogin }) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    // For handling backend URL, ideally use an env var or constant
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    // Helper to get CSRF token from cookies
    function getCookie(name) {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                // Does this cookie string begin with the name we want?
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        if (email && password) {
            try {
                // Use axios for login
                const response = await axios.post(`${API_URL}/dj-rest-auth/login/`, {
                    email: email,
                    password: password
                });

                // Login successful
                console.log("Login successful:", response.data);
                onLogin(response.data); 
            } catch (err) {
                console.error("Login Error:", err);
                if (err.response && err.response.data) {
                    const data = err.response.data;
                    if (data.non_field_errors) {
                        setError(data.non_field_errors[0]);
                    } else if (data.detail) {
                        setError(data.detail);
                    } else {
                        setError("Identifiants incorrects ou erreur serveur.");
                    }
                } else {
                    setError("Impossible de contacter le serveur.");
                }
            } finally {
                setLoading(false);
            }
        }
    };

    // Google OAuth would require a redirect or popup flow to specific backend endpoint or using a library.
    // Given the complexity of implementing correct OAuth flow manually in one go, 
    // we will set it to redirect to backend Google Login if button clicked?
    // Or just kept as placeholder until user configures API keys?
    // User asked to "create functions to manage google auth".
    // A simple way is to use `django-allauth` built-in templates or endpoints.
    // Since we are decoupling (React), we typically need a frontend lib to get token -> send to backend view `google_login`.
    // For now, let's keep the placeholder logic for OAuth but noted as TODO or basic redirect.

    // Google OAuth Logic

    const loginWithGoogle = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            console.log("Google Login Success:", tokenResponse);
            try {
                // Use axios for Google login (handles CSRF automatically via defaults)
                const response = await axios.post(`${API_URL}/api/google/`, {
                    access_token: tokenResponse.access_token,
                });

                console.log("Backend Google Auth Response:", response.data);
                onLogin(response.data); // Login successful in App
            } catch (err) {
                console.error("Google Auth Error:", err);
                
                let errorMsg = "Erreur connexion serveur après Google.";
                if (err.response) {
                    // The server responded with a status code that falls out of the range of 2xx
                    console.error("Backend refused Google connection:", err.response.data);
                    errorMsg = err.response.data.detail || err.response.data.error || "Erreur authentification Google.";
                    
                    // FORCE LOGOUT TO CLEAR STALE DATA
                    try {
                        await axios.post(`${API_URL}/dj-rest-auth/logout/`);
                    } catch (e) { console.warn("Force logout failed", e); }
                } else if (err.request) {
                    // The request was made but no response was received
                    errorMsg = "Le serveur ne répond pas. Vérifiez votre connexion.";
                } else {
                    errorMsg = `Erreur: ${err.message}`;
                }
                
                setError(errorMsg);
                // Navigation now handled by React Router
            }
        },
        onError: error => {
            console.error("Google Login Failed:", error);
            setError("Échec de la connexion Google.");
        }
    });

    const handleOAuthLogin = (provider) => {
        if (provider === 'Google') {
            loginWithGoogle();
        } else {
            alert("L'authentification " + provider + " n'est pas encore disponible.");
        }
    };

    return (
        <div className="login-container">
            <div className="simple-nav">
                <button className="brand-home-link" onClick={() => navigate('/')}>ARCHIVIZAI</button>
            </div>
            <div className="login-card">
                <h1 className="login-title">ARCHIVIZAI</h1>

                <form onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            className="input-field"
                            placeholder="votre@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="password">Mot de passe</label>
                        <input
                            type="password"
                            id="password"
                            className="input-field"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    {error && <div className="error-message" style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}

                    <div style={{ textAlign: 'right', marginBottom: '15px' }}>
                        <button type="button" onClick={() => navigate('/mot-de-passe-oublie')} style={{ background: 'none', border: 'none', color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}>
                            Mot de passe oublié ?
                        </button>
                    </div>

                    <button type="submit" className="login-btn" disabled={loading}>
                        {loading ? 'Connexion...' : 'Se connecter'}
                    </button>
                </form>

                <div className="divider">
                    <span>ou continuer avec</span>
                </div>

                <div className="oauth-buttons">
                    <button className="oauth-btn" onClick={() => handleOAuthLogin('Google')}>
                        <svg className="oauth-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Google
                    </button>

                </div>

                <div className="login-footer">
                    <p>Pas encore de compte ? <button onClick={() => navigate('/inscription')} className="link-btn">Créer un compte</button></p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
